import { useEffect, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { logger } from "@utilities/logger";

interface CategorySearchResult {
  id: string;
  slug: string;
  title: string;
  breadcrumbName?: string | null;
}

export interface CategorySearchItem {
  id: string;
  textValue: string;
  label: string;
  slug: string;
}

interface UseSfxCategorySearchParams {
  onCategorySearch?: (query: string, signal: AbortSignal) => Promise<{ results: CategorySearchResult[] }>;
  locale: string;
}

export function useSfxCategorySearch({ onCategorySearch, locale }: UseSfxCategorySearchParams) {
  const [query, setQuery] = useState("");
  const [searchItems, setSearchItems] = useState<CategorySearchItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [debouncedQuery] = useDebounceValue(query, 300);

  useEffect(() => {
    if (debouncedQuery.trim().length <= 2) {
      setSearchItems([]);
      setIsSearching(false);
      return;
    }

    if (!onCategorySearch) return;

    const controller = new AbortController();
    setIsSearching(true);

    onCategorySearch(debouncedQuery, controller.signal)
      .then((data: { results: CategorySearchResult[] }) => {
        setSearchItems(
          data.results.map(r => ({
            id: r.id,
            textValue: r.breadcrumbName || r.title,
            label: r.breadcrumbName || r.title,
            slug: r.slug,
          })),
        );
        setIsSearching(false);
        captureEvent(EventName.marketing_website_sound_effects_search, {
          search_query: debouncedQuery,
          search_results_count: data.results.length,
        });
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name !== "AbortError") {
          logger.error(err, "Error fetching category search results");
          setIsSearching(false);
        }
      });

    return () => controller.abort();
  }, [onCategorySearch, debouncedQuery, locale]);

  const clearSearch = () => {
    setQuery("");
    setSearchItems([]);
    setIsSearching(false);
  };

  return { query, setQuery, searchItems, isSearching, clearSearch };
}
